import { IconSearch, IconWarning, colors } from '@cabonline/ui-shared';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  GroupBase,
  MenuListProps,
  OptionProps,
  Props,
  default as ReactSelect,
  SingleValueProps,
  components,
} from 'react-select';

import { StyledRequired } from '../../form/form-item';
import { HelpText } from '../../input';
import { Label } from '../../label';
import {
  CustomInput,
  CustomOption,
  CustomSingleValue,
  OwnProps,
} from '../common';
import {
  Container,
  ErrorText,
  ErrorWrap,
  StyledSelectMenuHeader,
  StyledSelectSearchInput,
  getStyles,
  getThemeConfig,
} from '../styles';

export type SelectProps<
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>
> = Props<Option, IsMulti, Group> & OwnProps & {
  searchable?: boolean;
  searchPlaceholder?: string;
};

const DropdownIndicatorNull = () => null;

export const Select = <
  Option extends object,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>
>(
  props: SelectProps<Option, IsMulti, Group>
): React.ReactElement => {
  const {
    id,
    testID = '',
    label,
    hasError,
    isRequired,
    showSeparator,
    stopPropagation,
    hideCaret,
    darkMode,
    prefixIcon,
    sentenceCased = false,
    help,
    customStyles,
    searchable = false,
    searchPlaceholder = 'Search...',
    options,
    onMenuOpen,
    onMenuClose,
  } = props;
  const styles = getStyles<Option, IsMulti, Group>(
    props.hasError,
    darkMode,
    customStyles
  );

  const [searchTerm, setSearchTerm] = useState('');
  const [searchableMenuOpen, setSearchableMenuOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const isSearchInteractingRef = useRef(false);

  const filteredOptions = useMemo(() => {
    if (!searchable || !searchTerm) return options;
    const lower = searchTerm.toLowerCase();
    return (options ?? [])
      .map((opt) => {
        if ('options' in (opt as object)) {
          const group = opt as Group;
          const groupOptions = (group.options as Option[]).filter(
            (o) =>
              (o as { label?: string }).label?.toLowerCase().includes(lower) ??
              false
          );
          return { ...group, options: groupOptions };
        }
        const o = opt as Option & { label?: string };
        return (o.label?.toLowerCase().includes(lower) ?? false) ? opt : null;
      })
      .filter((opt): opt is Option | Group => {
        if (!opt) return false;
        if ('options' in (opt as object)) {
          return ((opt as { options: Option[] }).options).length > 0;
        }
        return true;
      });
  }, [searchable, searchTerm, options]);

  const handleMenuClose = useCallback(() => {
    if (isSearchInteractingRef.current) return;
    setSearchableMenuOpen(false);
    setSearchTerm('');
    if (searchInputRef.current) {
      searchInputRef.current.value = '';
    }
    onMenuClose?.();
  }, [onMenuClose]);

  const SingleValue = useCallback(
    (svProps: SingleValueProps<Option>) => (
      <CustomSingleValue
        {...svProps}
        sentenceCased={sentenceCased}
        prefixIcon={prefixIcon}
        testID={testID}
      />
    ),
    [sentenceCased, prefixIcon, testID]
  );

  const MenuListWithSearch = useCallback(
    (menuListProps: MenuListProps<Option, IsMulti, Group>) => (
      <components.MenuList {...menuListProps}>
        <StyledSelectMenuHeader>
          <IconSearch color={colors.gray2} width={18} height={18} />
          <StyledSelectSearchInput
            ref={searchInputRef}
            type="text"
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
            autoComplete="off"
            defaultValue=""
            onChange={(e) => setSearchTerm(e.target.value)}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              isSearchInteractingRef.current = true;
              const target = e.currentTarget;
              requestAnimationFrame(() => {
                target.focus();
                requestAnimationFrame(() => {
                  isSearchInteractingRef.current = false;
                });
              });
            }}
            onKeyDown={(e) => e.stopPropagation()}
          />
        </StyledSelectMenuHeader>
        {menuListProps.children}
      </components.MenuList>
    ),
    [searchPlaceholder]
  );

  const renderOption = useCallback(
    (optionProps: OptionProps<Option, IsMulti, Group>) =>
      CustomOption(optionProps, testID, sentenceCased),
    [testID, sentenceCased]
  );

  const componentsConfig = useMemo(
    () => ({
      Input: CustomInput,
      SingleValue,
      ...(!showSeparator ? { IndicatorSeparator: null } : {}),
      ...(hideCaret ? { DropdownIndicator: DropdownIndicatorNull } : {}),
      Option: renderOption,
      ...(searchable ? { MenuList: MenuListWithSearch } : {}),
    }),
    [SingleValue, showSeparator, hideCaret, renderOption, searchable, MenuListWithSearch]
  );

  return (
    <Container
      className="react-select-container"
      data-testid={testID}
      onClick={(event) => {
        if (stopPropagation) {
          event.preventDefault();
          event.stopPropagation();
        }
      }}
    >
      {label && (
        <Label htmlFor={id} data-testid={`${testID}-label`}>
          {label}
          {isRequired && <StyledRequired aria-hidden />}
        </Label>
      )}
      {help && <HelpText>{help}</HelpText>}
      {hasError && typeof hasError === 'string' && (
        <ErrorWrap>
          <IconWarning color={colors.red} />
          <ErrorText>{hasError}</ErrorText>
        </ErrorWrap>
      )}
      <ReactSelect
        {...props}
        styles={styles}
        theme={getThemeConfig()}
        id={`${id}-input-container`}
        inputId={id}
        aria-required={isRequired}
        isSearchable={searchable ? false : props.isSearchable}
        openMenuOnFocus={searchable ? false : (props.openMenuOnFocus ?? true)}
        {...(searchable ? {
          menuIsOpen: searchableMenuOpen,
          onMenuOpen: () => { setSearchableMenuOpen(true); onMenuOpen?.(); },
          onMenuClose: handleMenuClose,
        } : {
          onMenuClose: props.onMenuClose,
        })}
        options={filteredOptions}
        components={componentsConfig}
      />
    </Container>
  );
};
